import {useState} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Input} from '@/components/ui/input'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {HelpIcon} from '@/components/help-icon'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {CertificacionTab} from '@/components/certificacion-tab'
import {KairosMark} from '@/components/kairos-mark'
import {MicroLabel} from '@/components/period-selector'
import {useOperatorPage} from '@/components/no-access-state'
import {
  useIndicadores,
  useConfirmarMedicion,
  type FichaTablero,
  type IndicadorTablero,
} from '@/hooks/sgsst/indicadores/use-indicadores'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

type TabKey = 'fichas' | 'certificacion'

function valorMostrado(fila: FichaTablero): number | null {
  if (fila.ultima_medicion) return fila.ultima_medicion.valor
  return fila.propuesta ? fila.propuesta.valor : null
}

function RegistroDialog({
  businessId,
  fila,
  periodYear,
  onClose,
}: {
  businessId: string
  fila: FichaTablero
  periodYear: number
  onClose: () => void
}) {
  const {t} = useTranslation()
  const confirmar = useConfirmarMedicion(businessId, fila.ficha.codigo)
  const esAuto = fila.ficha.fuente === 'automatica'
  const [numerador, setNumerador] = useState('')
  const [denominador, setDenominador] = useState('')
  const [valor, setValor] = useState('')
  const [nota, setNota] = useState('')
  const [error, setError] = useState<string | null>(null)

  function guardar() {
    setError(null)
    const body = esAuto
      ? {period_year: periodYear}
      : {
          period_year: periodYear,
          numerador: numerador === '' ? null : Number(numerador),
          denominador: denominador === '' ? null : Number(denominador),
          valor: valor === '' ? null : Number(valor),
          nota: nota || null,
        }
    confirmar.mutate(body, {
      onSuccess: onClose,
      onError: (err) => setError(translateApiError(err)),
    })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="indicador-registro-dialog">
        <DialogHeader>
          <DialogTitle>{fila.ficha.nombre}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">{fila.ficha.definicion}</p>
          <p className="font-mono text-xs text-muted-foreground">{fila.ficha.formula}</p>
          {esAuto ? (
            <div className="surface p-4" data-testid="indicador-propuesta">
              <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                {t('indicadores.registro.valorPropuesto')}
              </p>
              <p className="mt-1 font-heading text-2xl text-primary">
                {fila.propuesta && fila.propuesta.valor !== null ? fila.propuesta.valor : t('indicadores.noAplica')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <MicroLabel>{t('indicadores.registro.numerador')}</MicroLabel>
                <Input
                  type="number"
                  value={numerador}
                  onChange={(e) => setNumerador(e.target.value)}
                  data-testid="indicador-numerador"
                />
              </label>
              <label className="space-y-1">
                <MicroLabel>{t('indicadores.registro.denominador')}</MicroLabel>
                <Input
                  type="number"
                  value={denominador}
                  onChange={(e) => setDenominador(e.target.value)}
                  data-testid="indicador-denominador"
                />
              </label>
              <label className="col-span-2 space-y-1">
                <MicroLabel>{t('indicadores.registro.valor')}</MicroLabel>
                <Input
                  type="number"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  data-testid="indicador-valor"
                />
              </label>
              <label className="col-span-2 space-y-1">
                <MicroLabel>{t('indicadores.registro.nota')}</MicroLabel>
                <Input
                  maxLength={tope('NOTA')}
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  data-testid="indicador-nota"
                />
              </label>
            </div>
          )}
          {error && (
            <p className="text-sm text-destructive" data-testid="indicador-registro-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={guardar} disabled={confirmar.isPending} data-testid="indicador-guardar">
            {esAuto ? t('indicadores.registro.confirmar') : t('indicadores.registro.guardar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function FichasTab({businessId, tablero, canEdit}: {businessId: string; tablero: IndicadorTablero; canEdit: boolean}) {
  const {t} = useTranslation()
  const [activa, setActiva] = useState<FichaTablero | null>(null)

  return (
    <section className="surface overflow-hidden" data-testid="indicadores-tabla">
      <div className="surface-divider flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <h2 className="font-heading text-base text-foreground">{t('indicadores.tabs.fichas')}</h2>
        <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          {t('indicadores.periodo', {year: tablero.period_year})}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="surface-divider border-b">
              {['indicador', 'tipo', 'ultimaMedicion', 'valor', 'meta', ''].map((col, i) => (
                <th
                  key={col || i}
                  className="px-4 py-2.5 text-left font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                  {col === 'ultimaMedicion' ? (
                    <span className="inline-flex items-center gap-1">
                      {t('indicadores.col.ultimaMedicion')}
                      <HelpIcon
                        label={t('common.help.iconLabelFor', {campo: t('indicadores.col.ultimaMedicion')})}
                        testId="indicadores-medicion-help">
                        {t('indicadores.medicionHelp')}
                      </HelpIcon>
                    </span>
                  ) : col ? (
                    t(`indicadores.col.${col}`)
                  ) : (
                    ''
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tablero.fichas.map((fila) => {
              const valorFinal = valorMostrado(fila)
              return (
                <tr
                  key={fila.ficha.codigo}
                  className="border-b border-border/60 transition-colors last:border-b-0 hover:bg-secondary/30"
                  data-testid={`indicador-fila-${fila.ficha.codigo}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{fila.ficha.nombre}</p>
                    <p className="text-xs text-muted-foreground">{fila.ficha.definicion}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Tag tone="neutral">{t(`indicadores.tipo.${fila.ficha.tipo}`)}</Tag>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {fila.ultima_medicion ? (
                      fila.ultima_medicion.valor !== null ? (
                        fila.ultima_medicion.valor
                      ) : (
                        t('indicadores.noAplica')
                      )
                    ) : (
                      <Tag tone="amber" data-testid={`indicador-pendiente-${fila.ficha.codigo}`}>
                        {fila.ficha.fuente === 'automatica'
                          ? t('indicadores.porConfirmar')
                          : t('indicadores.porRegistrar')}
                      </Tag>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono">
                    {valorFinal !== null ? valorFinal : t('indicadores.noAplica')}
                    {!fila.ultima_medicion && fila.propuesta && (
                      <span className="ml-1.5 font-sans text-[10px] text-muted-foreground">
                        {t('indicadores.propuesto')}
                      </span>
                    )}
                  </td>
                  {/* Sin columna de semáforo: ninguna ficha tiene meta numérica y el backend contesta neutral en todas. */}
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fila.ficha.meta_sugerida ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActiva(fila)}
                        data-testid={`indicador-registrar-${fila.ficha.codigo}`}>
                        {fila.ficha.fuente === 'automatica'
                          ? t('indicadores.registro.confirmar')
                          : t('indicadores.registro.registrar')}
                      </Button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {activa && (
        <RegistroDialog
          businessId={businessId}
          fila={activa}
          periodYear={tablero.period_year}
          onClose={() => setActiva(null)}
        />
      )}
    </section>
  )
}

export function IndicadoresPage() {
  const {t} = useTranslation()
  const {businessId} = useParams<{businessId: string}>()
  const {canEdit, guard} = useOperatorPage(businessId)
  const {data: tablero} = useIndicadores(businessId)
  const [tab, setTab] = useState<TabKey>('fichas')

  if (guard) return guard
  if (!businessId || !tablero) return null

  return (
    <div className="space-y-4" data-testid="indicadores-page">
      <div>
        <div className="flex items-center gap-2">
          <KairosMark />
          <MicroLabel>{t('indicadores.eyebrow')}</MicroLabel>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <h1 className="font-heading text-xl text-foreground">{t('indicadores.title')}</h1>
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('indicadores.title')})} testId="indicadores-tip">
            {t('indicadores.tip')}
          </HelpIcon>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="fichas" data-testid="tab-fichas">
            {t('indicadores.tabs.fichas')}
          </TabsTrigger>
          <TabsTrigger value="certificacion" data-testid="tab-certificacion">
            {t('indicadores.tabs.certificacion')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fichas" className="pt-4">
          <FichasTab businessId={businessId} tablero={tablero} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="certificacion" className="pt-4">
          <CertificacionTab businessId={businessId} artefacto="indicadores" />
        </TabsContent>
      </Tabs>
    </div>
  )
}
